import { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Tag, Input, App, Typography, Popconfirm } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, ReloadOutlined, UserAddOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Staff as StaffType, StaffStatus, StaffRole } from '@mygate/common';
import { staffService } from '@/services/staff';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTime } from '@/utils/date';
import { AddEditStaffModal } from '@/components/AddEditStaffModal';

const { Text } = Typography;

export function Staff() {
  const { message } = App.useApp();
  const [allStaff, setAllStaff] = useState<StaffType[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffType | null>(null);
  const { staff: currentStaff } = useAuth();

  useEffect(() => {
    fetchAllStaff();
  }, []);

  const fetchAllStaff = async () => {
    setLoading(true);
    try {
      const response = await staffService.fetchAllStaff({ pageSize: 100 });
      if (response.success && response.data) {
        setAllStaff(response.data.items);
      }
    } catch (error) {
      message.error('Failed to fetch staff members');
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = () => {
    setEditingStaff(null);
    setModalVisible(true);
  };

  const handleEditStaff = (staff: StaffType) => {
    setEditingStaff(staff);
    setModalVisible(true);
  };

  const handleDeleteStaff = async (id: string) => {
    try {
      await staffService.deleteStaff(id);
      message.success('Staff member deleted successfully');
      fetchAllStaff();
    } catch (error) {
      message.error('Failed to delete staff member');
    }
  };

  const filteredStaff = allStaff.filter(staff => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      staff.phoneNumber?.toLowerCase().includes(search) ||
      staff.email?.toLowerCase().includes(search) ||
      staff.firstName?.toLowerCase().includes(search) ||
      staff.lastName?.toLowerCase().includes(search)
    );
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      [StaffStatus.ACTIVE]: 'green',
      [StaffStatus.SUSPENDED]: 'red',
    };
    return colors[status] || 'default';
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      [StaffRole.ADMIN]: 'red',
      [StaffRole.SUPPORT]: 'blue',
    };
    return colors[role] || 'default';
  };

  const columns: ColumnsType<StaffType> = [
    {
      title: 'Mitarbeiter & Kontakt',
      key: 'identity',
      width: 300,
      render: (_, record) => (
        <div className="table-cell-stack">
          <Button type="link" className="table-cell-link" onClick={() => handleEditStaff(record)}>
            {[record.firstName, record.lastName].filter(Boolean).join(' ') || record.phoneNumber}
          </Button>
          <Text className="table-cell-muted table-cell-truncate" title={record.email}>
            {record.email || 'Keine E-Mail'}
          </Text>
          <Text className="table-cell-muted">{record.phoneNumber}</Text>
        </div>
      ),
    },
    {
      title: 'Zugriff',
      key: 'access',
      width: 180,
      render: (_, record) => (
        <div className="table-cell-stack">
          <Tag color={getRoleColor(record.role)}>{record.role}</Tag>
          <Tag color={getStatusColor(record.status)}>{record.status}</Tag>
        </div>
      ),
    },
    {
      title: 'Aktivität',
      key: 'activity',
      width: 230,
      render: (_, record) => (
        <div className="table-cell-stack">
          <Text>{record.lastLoginAt ? formatDateTime(record.lastLoginAt) : 'Noch kein Login'}</Text>
          <Text className="table-cell-muted">Erstellt: {formatDateTime(record.createdAt)}</Text>
        </div>
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      width: 104,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            aria-label="Mitarbeiter bearbeiten"
            icon={<EditOutlined />}
            onClick={() => handleEditStaff(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this staff member?"
            onConfirm={() => handleDeleteStaff(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.id === currentStaff?.id}
          >
            <Button
              type="link"
              danger
              aria-label="Mitarbeiter löschen"
              icon={<DeleteOutlined />}
              disabled={record.id === currentStaff?.id}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Team und Zugriffsrechte verwalten.</Typography.Text>

        <Tag>{allStaff.length} Mitarbeiter</Tag>
      </div>

      <>
        <Card>
          <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
            <Space wrap>
              <Input
                placeholder="Name, E-Mail oder Telefon suchen"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              <Button icon={<ReloadOutlined />} onClick={fetchAllStaff} loading={loading}>
                Aktualisieren
              </Button>
            </Space>
            <Button type="primary" icon={<UserAddOutlined />} onClick={handleAddStaff}>
              Mitarbeiter hinzufügen
            </Button>
          </Space>

          <Table
            tableLayout="fixed"
            scroll={{ x: 814 }}
            columns={columns}
            dataSource={filteredStaff}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}–${range[1]} von ${total} Mitarbeitern`,
            }}
          />
        </Card>

        <AddEditStaffModal
          visible={modalVisible}
          staff={editingStaff}
          isSelfEdit={editingStaff?.id === currentStaff?.id}
          onClose={() => {
            setModalVisible(false);
            setEditingStaff(null);
          }}
          onSuccess={() => {
            setModalVisible(false);
            setEditingStaff(null);
            fetchAllStaff();
          }}
        />
      </>
    </>
  );
}
